import { CameraIcon, StarFourIcon, UserIcon } from '@phosphor-icons/react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      xs: 'size-6 text-micro',
      sm: 'size-8 text-xs',
      md: 'size-10 text-sm',
      lg: 'size-12 text-base',
      xl: 'size-16 text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type AvatarSize = NonNullable<VariantProps<typeof avatarVariants>['size']>;
export type AvatarTier = 'pro' | 'pro_max';

// Tier identity uses the same quiet blue family as the composer model picker:
// Pro gets a restrained ring/mark; Pro Max gets the stronger version. Each
// tier owns one continuous ring — an offset creates a background-coloured moat
// that reads as a broken white seam around circular fallbacks.
const tierRingClasses: Record<AvatarTier, Record<AvatarSize, string>> = {
  pro: {
    xs: 'ring-1 ring-tier',
    sm: 'ring-1 ring-tier',
    md: 'ring-1 ring-tier',
    lg: 'ring-1 ring-tier',
    xl: 'ring-1 ring-tier',
  },
  pro_max: {
    xs: 'ring-1 ring-info',
    sm: 'ring-1 ring-info',
    md: 'ring-2 ring-info',
    lg: 'ring-2 ring-info',
    xl: 'ring-2 ring-info',
  },
};

const proStarClasses: Record<AvatarSize, string> = {
  xs: '-right-0.5 -top-0.5 size-2.5',
  sm: '-right-0.5 -top-0.5 size-3',
  md: '-right-0.5 -top-0.5 size-3.5',
  lg: '-right-0.5 -top-0.5 size-4',
  xl: '-right-1 -top-1 size-5',
};

const tierStarSize: Record<AvatarTier, Record<AvatarSize, string>> = {
  // Pro is one notch smaller — the outline weight already feels lighter.
  pro: {
    xs: 'size-2',
    sm: 'size-2.5',
    md: 'size-3',
    lg: 'size-3.5',
    xl: 'size-4',
  },
  pro_max: {
    xs: 'size-2.5',
    sm: 'size-3',
    md: 'size-3.5',
    lg: 'size-4',
    xl: 'size-5',
  },
};

interface AvatarProps
  extends
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string | null;
  alt?: string;
  fallback?: React.ReactNode;
  name?: string | null;
  email?: string | null;
  /**
   * Premium tier treatment.
   * - 'pro'     → subtle blue ring + filled tier mark
   * - 'pro_max' → stronger blue ring + larger filled tier mark
   * Free users omit this prop entirely.
   */
  tier?: AvatarTier;
  /** @deprecated Use `tier="pro_max"` instead. Kept for backwards-compat. */
  pro?: boolean;
  monochrome?: boolean;
  uploadable?: boolean;
  showIcon?: boolean;
  fallbackClassName?: string;
  /** Optional fit treatment for a supplied image, e.g. workspace logos. */
  imageClassName?: string;
  onUploadClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

// `name.split(' ')` yields empty strings for every run of whitespace, so
// `word[0]` is `undefined` and the join collapses to ''. A profile whose
// full_name is "   " therefore rendered a COMPLETELY BLANK circle — no
// initials, no icon — because the caller had already branched on `name`
// being truthy. Split on runs of whitespace and drop empties so a
// whitespace-only name is treated as no name at all.
function getInitials(name?: string | null, email?: string | null): string {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length > 0) {
    return words
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  const trimmedEmail = email?.trim();
  if (trimmedEmail) {
    return trimmedEmail.slice(0, 2).toUpperCase();
  }
  return 'U';
}

const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  (
    {
      className,
      size,
      src,
      alt,
      fallback,
      name,
      email,
      tier,
      pro,
      monochrome,
      uploadable,
      showIcon = true,
      fallbackClassName,
      imageClassName,
      onUploadClick,
      children,
      ...props
    },
    ref,
  ) => {
    const resolvedSize = size ?? 'md';
    const resolvedTier: AvatarTier | undefined = tier ?? (pro ? 'pro_max' : undefined);
    // Trim before branching: an all-whitespace name is not an identity, and
    // treating it as one is what sent "   " down the initials path.
    const identityName = name?.trim() || undefined;
    const identityEmail = email?.trim() || undefined;
    const fallbackContent =
      fallback ??
      (identityName || identityEmail ? (
        getInitials(identityName, identityEmail)
      ) : showIcon ? (
        <UserIcon className="h-[60%] w-[60%]" weight="regular" />
      ) : (
        'U'
      ));

    const hasManagedChildren = children !== undefined;

    // The tier mark must render OUTSIDE the AvatarPrimitive.Root
    // because Root has `overflow-hidden` (required to clip the avatar image
    // to a circle) — a negative-offset star inside Root gets clipped to
    // the circle bounds and disappears behind the rim. Wrap Root + Star
    // in a relative span so the star can sit on top with positive z-index
    // outside the clipping container.
    const root = (
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          avatarVariants({ size: resolvedSize }),
          resolvedTier && tierRingClasses[resolvedTier][resolvedSize],
          uploadable && 'group',
          className,
        )}
        {...props}
      >
        {hasManagedChildren ? (
          children
        ) : (
          <>
            {src && (
              <AvatarPrimitive.Image
                src={src}
                alt={alt ?? name ?? 'Avatar'}
                className={cn(
                  'aspect-square h-full w-full',
                  monochrome && 'grayscale',
                  imageClassName,
                )}
              />
            )}
            <AvatarPrimitive.Fallback
              className={cn(
                'flex h-full w-full items-center justify-center rounded-full bg-muted',
                fallbackClassName,
              )}
            >
              {fallbackContent}
            </AvatarPrimitive.Fallback>
          </>
        )}
        {uploadable && (
          <button
            type="button"
            aria-label="Upload avatar"
            onClick={onUploadClick}
            className="absolute inset-0 z-20 flex items-center justify-center rounded-full bg-background/60 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CameraIcon className="size-1/3 text-foreground" weight="regular" />
          </button>
        )}
      </AvatarPrimitive.Root>
    );

    if (!resolvedTier) return root;

    const isMax = resolvedTier === 'pro_max';
    return (
      <span className="relative inline-flex shrink-0">
        {root}
        <StarFourIcon
          className={cn(
            'absolute z-20 pointer-events-none',
            proStarClasses[resolvedSize],
            tierStarSize[resolvedTier][resolvedSize],
            isMax ? 'text-info' : 'text-tier',
          )}
          weight="fill"
          aria-hidden
        />
      </span>
    );
  },
);
Avatar.displayName = 'Avatar';

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
));
AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = 'AvatarFallback';

export interface AvatarGroupItem {
  id: string;
  src?: string | null;
  name?: string | null;
  email?: string | null;
  fallback?: string;
  tier?: AvatarTier;
  /** @deprecated Use `tier="pro_max"` instead. */
  pro?: boolean;
}

interface AvatarGroupProps {
  items: AvatarGroupItem[];
  max?: number;
  size?: VariantProps<typeof avatarVariants>['size'];
  className?: string;
  showCount?: boolean;
  overlap?: 'tight' | 'normal' | 'loose' | 'open';
}

const overlapClasses: Record<NonNullable<AvatarGroupProps['overlap']>, string> = {
  tight: '-space-x-3',
  normal: '-space-x-2',
  loose: '-space-x-1',
  open: '-space-x-0.5',
};

const AvatarGroup: React.FC<AvatarGroupProps> = ({
  items,
  max = 3,
  size = 'sm',
  className,
  showCount = true,
  overlap = 'normal',
}) => {
  const visible = items.slice(0, max);
  const remaining = Math.max(0, items.length - max);

  return (
    <div className={cn('flex items-center', className)}>
      <div className={cn('flex', overlapClasses[overlap])}>
        {visible.map((item, index) => (
          <Avatar
            key={item.id}
            size={size}
            src={item.src}
            name={item.name}
            email={item.email}
            fallback={item.fallback}
            tier={item.tier}
            pro={item.pro}
            className={cn(
              'ring-2 ring-background',
              index === 0 && 'z-30',
              index === 1 && 'z-20',
              index === 2 && 'z-10',
            )}
          />
        ))}
        {showCount && remaining > 0 && (
          <div
            className={cn(
              avatarVariants({ size }),
              'z-0 flex items-center justify-center bg-muted text-muted-foreground font-medium ring-2 ring-background',
            )}
          >
            +{remaining}
          </div>
        )}
      </div>
    </div>
  );
};

export function createAvatarItems(
  data: Array<{
    id?: string;
    avatar_url?: string | null;
    avatarUrl?: string | null;
    profile_picture_url?: string | null;
    name?: string | null;
    email?: string | null;
    platform?: string;
  }>,
): AvatarGroupItem[] {
  return data.map((item, index) => ({
    id: item.id || `avatar-${index}`,
    src: item.avatar_url || item.avatarUrl || item.profile_picture_url,
    name: item.name,
    email: item.email,
    fallback: item.platform ? item.platform[0].toUpperCase() : undefined,
  }));
}

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup, avatarVariants };
